/**
 * Two-factor authentication API client.
 *
 * Uses a local request helper that mirrors the shared `request` function in
 * `services/api.ts` (CSRF token on mutating methods, ApiError on failure) but
 * is self-contained so the private `request` function need not be exported.
 *
 * CSRF wiring: call `bindTwoFactorCsrf(getter)` from `services/api.ts` so
 * this module always reads the current in-memory token without needing to
 * export the private `csrfToken` variable.
 */
import type {
  TwoFactorStatus,
  TwoFactorSetupResponse,
  TwoFactorSetupConfirmResponse,
  TwoFactorRegenerateBackupCodesResponse,
  TwoFactorVerifyResponse,
} from '@atlas/shared';
import { ApiError } from '../services/ApiError';

let _getCsrfToken: (() => string | null) = () => null;

/**
 * Called once by `services/api.ts` to wire up CSRF token access.
 * Pass a getter that returns the current in-memory token.
 */
export function bindTwoFactorCsrf(getToken: () => string | null): void {
  _getCsrfToken = getToken;
}

const API_BASE = (import.meta as { env?: Record<string, string> }).env?.VITE_API_URL ?? 'http://localhost:3000/api/v1';
const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

async function req<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method || 'GET').toUpperCase();
  const token = _getCsrfToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init.headers as Record<string, string> | undefined),
  };

  if (MUTATING.has(method) && token) {
    headers['x-csrf-token'] = token;
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers,
  });

  const rawBody = await response.text();
  let data: { data?: unknown; error?: { code?: string; message?: string; details?: Record<string, unknown> } } | null = null;
  if (rawBody) {
    try {
      data = JSON.parse(rawBody);
    } catch {
      data = null;
    }
  }

  if (!response.ok) {
    if (response.status === 401) {
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    }
    throw new ApiError(
      response.status,
      data?.error?.code || 'UNKNOWN_ERROR',
      data?.error?.message || `Request failed (${response.status})`,
      data?.error?.details,
    );
  }

  return data?.data as T;
}

export const twoFactor = {
  // ── User self-service ────────────────────────────────────────────────────
  status: () =>
    req<TwoFactorStatus>('/auth/2fa/status'),

  setup: () =>
    req<TwoFactorSetupResponse>('/auth/2fa/setup', { method: 'POST' }),

  confirmSetup: (code: string) =>
    req<TwoFactorSetupConfirmResponse>('/auth/2fa/setup/confirm', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),

  verify: (code: string) =>
    req<TwoFactorVerifyResponse>('/auth/2fa/verify', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),

  disable: (currentPassword: string, code: string) =>
    req<null>('/auth/2fa/disable', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, code }),
    }),

  regenerateBackupCodes: (code: string) =>
    req<TwoFactorRegenerateBackupCodesResponse>('/auth/2fa/backup-codes/regenerate', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),

  requestRecovery: (email: string) =>
    req<null>('/auth/2fa/recovery/request', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  consumeRecovery: (token: string, newPassword: string) =>
    req<{ user: { userId: string; email: string; name: string; roles: string[] } }>(
      '/auth/2fa/recovery/consume',
      {
        method: 'POST',
        body: JSON.stringify({ token, newPassword }),
      },
    ),

  // ── Admin ────────────────────────────────────────────────────────────────
  setRequired: (userId: string, required: boolean) =>
    req<unknown>(`/users/${encodeURIComponent(userId)}/2fa/required`, {
      method: 'PUT',
      body: JSON.stringify({ required }),
    }),

  adminReset: (userId: string) =>
    req<null>(`/users/${encodeURIComponent(userId)}/2fa/reset`, { method: 'POST' }),

  getGlobalSetting: () =>
    req<{ requireTwoFactorForAdmins: boolean }>('/users/settings/two-factor'),

  setGlobalSetting: (requireTwoFactorForAdmins: boolean) =>
    req<{ requireTwoFactorForAdmins: boolean }>('/users/settings/two-factor', {
      method: 'PUT',
      body: JSON.stringify({ requireTwoFactorForAdmins }),
    }),
};
